<template>
  <v-card class="pa-4 rounded-lg" outlined>
    <div class="d-flex align-center justify-space-between mb-4">
      <h3 class="text-h6 mb-0">{{ title }}</h3>
      <v-btn class="btn-get-code rounded-lg" small @click="triggerFileInput">
        <v-icon left>mdi-layers-plus</v-icon> افزودن لایه جدید
      </v-btn>
      <input
        type="file"
        ref="fileInput"
        style="display: none"
        accept="image/*"
        @change="addCustomLayer"
      />
    </div>

    <!-- محفظه بوم Fabric.js -->
    <div
      class="preview-wrapper d-flex justify-center"
      ref="previewContainer"
      style="background: #e0e0e0; border-radius: 8px; overflow: hidden"
    >
      <canvas :id="'fabric-canvas-' + uniqueId"></canvas>
    </div>

    <div v-if="hasSelection" class="d-flex justify-center mt-3 gap-2">
      <v-btn
        icon
        class="btn-download rounded-pill"
        small
        @click="bringForward"
        title="انتقال به لایه جلو"
      >
        <v-icon small>mdi-arrange-bring-forward</v-icon>
      </v-btn>
      <v-btn
        icon
        class="btn-download rounded-pill"
        small
        @click="sendBackward"
        title="انتقال به لایه عقب"
      >
        <v-icon small>mdi-arrange-send-backward</v-icon>
      </v-btn>
      <v-btn
        icon
        small
        class="btn-delete rounded-pill"
        @click="deleteSelected"
        title="حذف لایه"
      >
        <v-icon small>mdi-delete</v-icon>
      </v-btn>
    </div>
  </v-card>
</template>

<script>
import * as fabric from "fabric";

export default {
  name: "TemplatePositionEditor",
  props: {
    title: { type: String, default: "پیش‌نمایش" },
    bgImage: { type: String, default: "" },
    qrImage: { type: String, default: "" },
    value: { type: Object, required: true },
    labelConfig: { type: Object, default: () => ({ enabled: false }) },
    labelPos: { type: Object, default: () => ({ x: 50, y: 80 }) },
  },
  data() {
    return {
      uniqueId: Math.random().toString(36).substr(2, 9),
      hasSelection: false,
      qrImageObj: null,
      textObj: null,
      previewScale: 1,
    };
  },
  watch: {
    labelPos: {
      deep: true,
      handler() {
        this.updateTextLayer();
      },
    },
    labelConfig: {
      deep: true,
      handler() {
        this.updateTextLayer();
      },
    },
    bgImage(newVal) {
      if (newVal) {
        this.setCanvasBackground(newVal);
      } else if (this.canvas) {
        this.canvas.backgroundImage = null;
        this.canvas.renderAll();
      }
    },
    qrImage(newVal) {
      if (newVal) {
        this.addQrLayer(newVal);
      } else if (this.canvas && this.qrImageObj) {
        this.canvas.remove(this.qrImageObj);
        this.qrImageObj = null;
        this.canvas.renderAll();
      }
    },
    "value.shape": {
      handler(newShape) {
        this.applyClipPath(newShape);
      },
      deep: true,
    },
  },
  created() {
    this.canvas = null;
  },
  async mounted() {
    await this.$nextTick();
    this.initCanvas();

    if (this.bgImage) {
      await this.setCanvasBackground(this.bgImage);
    }
    if (this.qrImage) {
      this.addQrLayer(this.qrImage);
    }
  },
  methods: {
    updateTextLayer() {
      if (!this.canvas) return;

      const displayText = this.labelConfig.textValue || "0000";

      if (!this.labelConfig.enabled) {
        if (this.textObj) {
          this.canvas.remove(this.textObj);
          this.textObj = null;
          this.canvas.renderAll();
        }
        return;
      }

      const scaledFontSize = this.labelConfig.fontSize * this.previewScale;

      if (!this.textObj) {
        this.textObj = new fabric.Text(displayText, {
          originX: "center",
          originY: "center",
          transparentCorners: false,
          cornerColor: "#e91e63",
        });

        this.canvas.add(this.textObj);
      }

      this.textObj.set({
        fill: this.labelConfig.color,
        textBackgroundColor: this.labelConfig.backgroundColor,
        fontSize: scaledFontSize,
        fontFamily: this.labelConfig.fontFamily,
        text: displayText,
        left: (this.labelPos.x / 100) * this.canvas.width,
        top: (this.labelPos.y / 100) * this.canvas.height,
        angle: this.labelPos.angle || 0,
        scaleX: this.labelPos.scaleX || 1,
        scaleY: this.labelPos.scaleY || 1,
      });

      this.canvas.renderAll();
    },
    getCustomLayers() {
      if (!this.canvas) return [];

      const layers = [];
      const cw = this.canvas.width;
      const ch = this.canvas.height;

      this.canvas.getObjects().forEach((obj) => {
        if (obj === this.qrImageObj) return;

        if (obj.type === "image") {
          // دریافت نقطه مرکزی دقیق آبجکت
          const center = obj.getCenterPoint();

          layers.push({
            src: obj.getSrc(),
            // درصد مختصات نقطه مرکزی
            cxPercent: (center.x / cw) * 100,
            cyPercent: (center.y / ch) * 100,
            // محاسبه ابعاد واقعی با اعمال Scale
            widthPercent: ((obj.width * obj.scaleX) / cw) * 100,
            heightPercent: ((obj.height * obj.scaleY) / ch) * 100,
            angle: obj.angle || 0,
          });
        }
      });
      return layers;
    },
    updateConfigFromCanvas() {
      const activeObj = this.canvas.getActiveObject();

      if (!activeObj) return;

      if (this.textObj && activeObj === this.textObj) {
        const tX = (activeObj.left / this.canvas.width) * 100;
        const tY = (activeObj.top / this.canvas.height) * 100;

        this.$emit("update:labelPos", {
          x: tX,
          y: tY,
          angle: activeObj.angle || 0,
          scaleX: activeObj.scaleX || 1,
          scaleY: activeObj.scaleY || 1,
        });

        return;
      }

      if (activeObj === this.qrImageObj) {
        const boundingBox = activeObj.getBoundingRect();

        const updated = { ...this.value };
        updated.x = (boundingBox.left / this.canvas.width) * 100;
        updated.y = (boundingBox.top / this.canvas.height) * 100;
        updated.width =
          ((activeObj.width * activeObj.scaleX) / this.canvas.width) * 100;
        updated.height =
          ((activeObj.height * activeObj.scaleY) / this.canvas.height) * 100;

        this.$emit("input", updated);
      }
    },

    initCanvas() {
      this.canvas = new fabric.Canvas("fabric-canvas-" + this.uniqueId, {
        width: 600,
        height: 378,
        preserveObjectStacking: true,
      });

      this.canvas.on("selection:created", () => (this.hasSelection = true));
      this.canvas.on("selection:cleared", () => (this.hasSelection = false));
      this.canvas.on("object:modified", this.updateConfigFromCanvas);
    },

    async setCanvasBackground(url) {
      if (!this.canvas) return;

      try {
        const img = await fabric.Image.fromURL(url);

        const displayMaxWidth = this.$refs.previewContainer.clientWidth || 600;

        const scale = displayMaxWidth / img.width;

        this.previewScale = scale;

        const newCanvasWidth = img.width * scale;
        const newCanvasHeight = img.height * scale;

        this.canvas.setDimensions({
          width: newCanvasWidth,
          height: newCanvasHeight,
        });

        img.set({
          scaleX: scale,
          scaleY: scale,
          originX: "left",
          originY: "top",
        });

        this.canvas.backgroundImage = img;

        this.canvas.renderAll();

        this.updateTextLayer();
      } catch (error) {
        console.error("Error loading background image:", error);
      }
    },
    async addQrLayer(url) {
      if (!this.canvas) return;
      try {
        if (this.qrImageObj) {
          this.canvas.remove(this.qrImageObj);
          this.qrImageObj = null;
        }

        const img = await fabric.Image.fromURL(url);
        this.qrImageObj = img;

        const qrW = (this.value.width / 100) * this.canvas.width;
        const qrAspectRatio = img.width / img.height;
        const qrH = qrW / qrAspectRatio;

        const scaleX = qrW / img.width;
        const scaleY = qrH / img.height;

        img.set({
          left: (this.value.x / 100) * this.canvas.width,
          top: (this.value.y / 100) * this.canvas.height,
          scaleX: scaleX,
          scaleY: scaleY,
          originX: "left",
          originY: "top",
          cornerColor: "#1976d2",
          transparentCorners: false,
        });

        this.applyClipPath(this.value.shape);

        this.canvas.add(img);
        this.canvas.setActiveObject(img);
        this.canvas.renderAll();
      } catch (error) {
        console.error("Error loading QR image:", error);
      }
    },

    applyClipPath(shape) {
      if (!this.qrImageObj) return;

      if (shape === "circle") {
        const radius =
          Math.min(this.qrImageObj.width, this.qrImageObj.height) / 2;
        const clipPath = new fabric.Circle({
          radius: radius,
          originX: "center",
          originY: "center",
          left: this.qrImageObj.width / 2,
          top: this.qrImageObj.height / 2,
        });
        this.qrImageObj.set("clipPath", clipPath);
      } else {
        this.qrImageObj.set("clipPath", null);
      }

      if (this.canvas) {
        this.canvas.renderAll();
      }
    },

    triggerFileInput() {
      this.$refs.fileInput.click();
    },

    addCustomLayer(event) {
      const file = event.target.files[0];
      if (!file || !this.canvas) return;

      const reader = new FileReader();
      reader.onload = async (f) => {
        try {
          const img = await fabric.Image.fromURL(f.target.result);
          img.set({ left: 50, top: 50 });
          img.scaleToWidth(150);

          this.canvas.add(img);
          this.canvas.setActiveObject(img);
          this.canvas.renderAll();
        } catch (error) {
          console.error("Error adding custom layer:", error);
        }
      };
      reader.readAsDataURL(file);
      event.target.value = "";
    },

    bringForward() {
      const activeObject = this.canvas.getActiveObject();
      if (activeObject) {
        this.canvas.bringForward(activeObject);
        this.canvas.renderAll();
      }
    },
    sendBackward() {
      const activeObject = this.canvas.getActiveObject();
      if (activeObject) {
        this.canvas.sendBackwards(activeObject);
        this.canvas.renderAll();
      }
    },
    deleteSelected() {
      const activeObjects = this.canvas.getActiveObjects();
      if (activeObjects.length) {
        activeObjects.forEach((obj) => {
          if (obj === this.qrImageObj) this.qrImageObj = null;
          this.canvas.remove(obj);
        });
        this.canvas.discardActiveObject();
        this.canvas.renderAll();
      }
    },

    exportCanvasToDataURL(format = "png") {
      if (!this.canvas) return "";
      this.canvas.discardActiveObject();
      this.canvas.renderAll();
      return this.canvas.toDataURL({
        format: format,
        quality: 1,
        multiplier: 2,
      });
    },
  },
};
</script>

<style scoped>
.preview-wrapper {
  /* aspect-ratio: 1.586 / 1; */
  width: 100%;
}
</style>
